<script setup>
import { ref, computed, onMounted } from 'vue'
import { useEventListener } from '@vueuse/core'
import * as bootstrap from 'bootstrap'

const props = defineProps({
  canvasId: {
    type: String,
    default: 'baseOffCanvas'
  },
  canvasPosition: {
    type: String,
    default: 'end',
    validator: (value) => ['start', 'end', 'top', 'bottom'].includes(value)
  },
  width: {
    type: String,
    default: '511px'
  },
  closeOnOutsideClick: {
    type: Boolean,
    default: true
  },
  showCloseButton: {
    type: Boolean,
    default: true
  },
  zIndex: {
    type: Number,
    default: 1100
  },
  /**
   * Drops the app-wide navbar offset so the panel spans the whole viewport. For pages
   * rendered without the navbar (e.g. /transformTracker), where that offset leaves the
   * panel starting 61px down the screen with nothing above it.
   */
  fullHeight: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['show', 'hide', 'outside-click'])

/*
 * `body .offcanvas.offcanvas-end { top: 61px }` in custom.scss parks every side panel below
 * the navbar. An inline `top` is what reliably wins over that selector, so the override
 * lives here rather than in a scoped rule. Only the side positions are offset by it — top /
 * bottom panels are sized by Bootstrap from the opposite edge.
 */
const canvasStyle = computed(() => {
  const style = {
    '--bs-offcanvas-width': props.width,
    zIndex: props.zIndex
  }
  if (props.fullHeight && ['start', 'end'].includes(props.canvasPosition)) {
    style.top = '0'
    style.height = '100%'
  }
  return style
})

const offcanvasRef = ref(null)
const offcanvasInstance = ref(null)
const isVisible = ref(false)

// Bootstrap's native events (auto-cleanup via useEventListener)
useEventListener(offcanvasRef, 'shown.bs.offcanvas', onShown)
useEventListener(offcanvasRef, 'hidden.bs.offcanvas', onHidden)

onMounted(() => {
  if (offcanvasRef.value) {
    offcanvasInstance.value = new bootstrap.Offcanvas(offcanvasRef.value, {
      keyboard: true,
      backdrop: false,
      scroll: true,
    })
  }
})

// Document click listener for outside clicks (conditionally active)
const cleanupClickListener = ref(null)

function onShown() {
  isVisible.value = true
  emit('show')
  if (props.closeOnOutsideClick) {
    cleanupClickListener.value = useEventListener(document, 'click', handleDocumentClick)
  }
}

function onHidden() {
  isVisible.value = false
  emit('hide')
  if (cleanupClickListener.value) {
    cleanupClickListener.value()
    cleanupClickListener.value = null
  }
}

function handleDocumentClick(event) {
  const clickedElement = event.target
  const offcanvasElement = offcanvasRef.value

  if (!offcanvasElement) return

  // If clicking inside ANY offcanvas, don't handle this click
  const clickedInsideAnyOffcanvas = clickedElement.closest('.offcanvas') !== null
  if (clickedInsideAnyOffcanvas) {
    return
  }

  const cursorStyle = window.getComputedStyle(clickedElement).cursor
  const isTooltipTrigger = clickedElement.closest('[data-bcs-tooltip-trigger="true"]') !== null
  const hasNoCloseClass = clickedElement.closest('.no-close') !== null
  const isInsideOffcanvas = offcanvasElement.contains(clickedElement)
  const isPointerCursor = cursorStyle === 'pointer'

  // Close if:
  // - Clicked on a tooltip trigger, OR
  // - Clicked outside the offcanvas AND cursor is not pointer AND element doesn't have .no-close
  const shouldClose = isTooltipTrigger || (!isInsideOffcanvas && !isPointerCursor && !hasNoCloseClass)

  if (shouldClose) {
    emit('outside-click', event)
    hide()
  }
}

async function show() {
  if (offcanvasInstance.value) {
    await offcanvasInstance.value.show()
  }
}

async function hide() {
  if (offcanvasInstance.value) {
    await offcanvasInstance.value.hide()
  }
}

function toggle() {
  if (isVisible.value) {
    hide()
  } else {
    show()
  }
}

defineExpose({
  show,
  hide,
  toggle,
  isVisible
})
</script>

<template>
  <div
    :id="canvasId"
    ref="offcanvasRef"
    :class="['offcanvas', `offcanvas-${canvasPosition}`]"
    tabindex="-1"
    :aria-labelledby="`${canvasId}-label`"
    :style="canvasStyle"
  >
    <div class="offcanvas-header">
      <slot name="header">
        <h5 :id="`${canvasId}-label`" class="offcanvas-title">
          <slot name="title"></slot>
        </h5>
      </slot>
      <div
        v-if="showCloseButton"
        type="button"
        class="close-panel-button"
        @click="hide"
      >
        <b>Close Panel</b>
        <i class="bi bi-x-lg"></i>
      </div>
    </div>

    <div class="offcanvas-body">
      <slot></slot>
    </div>

    <div v-if="$slots.footer" class="offcanvas-footer">
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
.close-panel-button {
  padding: 0;
  color: var(--bs-primary);
  font-size: 14px;
  flex-shrink: 0;
  cursor: pointer;

  i {
    font-size: 11px;
    padding-left: 5px;
    -webkit-text-stroke: 1px;
  }
}

.offcanvas-footer {
  padding: 1rem;
  background-color: var(--bs-gray-200);
}
</style>
